/**
 * Query Key Factory — Centralized TanStack Query Key Management
 *
 * Go equivalent: Structured cache keys (prevents key collision / typo drift)
 *
 * All queryKey arrays should be constructed through this factory.
 * Benefits:
 * - Single source of truth for cache key structure
 * - Invalidation helpers (invalidate all estate queries, etc.)
 * - Compile-time safety via typed parameters
 * - Prevents string typo drift across components
 *
 * Pattern follows TanStack Query's recommended "query key factory" approach:
 * @see https://tanstack.com/query/latest/docs/framework/react/community/lukemorales-query-key-factory
 *
 * @example
 * ```ts
 * import { queryKeys } from '@/shared/lib/query-keys';
 *
 * // In a component:
 * useQuery({ queryKey: queryKeys.estates.suggest(query), ... });
 *
 * // Invalidation:
 * queryClient.invalidateQueries({ queryKey: queryKeys.estates.all });
 * ```
 */

import type { MapBounds } from '@/shared/lib/geo/bounds';
import type { ListingsFilterSet } from '@/shared/lib/listings/listings-filter-set';
import { messageThreadKey } from '@letsgethome/lib/conversations/message-store';
import type { TransactionModeValue } from '@letsgethome/lib/types/listings';
import type {
  ConversationId,
  DistrictId,
  EstateId,
  ExactInt,
  ListingId,
  PublicId,
} from '@letsgethome/types/branded';
import type { JsonSafeGuard } from '@letsgethome/types/json-safe';

import type { SaveableKey } from './saved/key';

// =============================================================================
// Query Key Factory
// =============================================================================

export const queryKeys = {
  // ---------------------------------------------------------------------------
  // Estates
  // ---------------------------------------------------------------------------
  estates: {
    /** Root key for all estate-related queries */
    all: ['estates'] as const,
    /** Estate name suggestions (autocomplete) */
    suggest: (query: string) => ['estate-suggest', query] as const,
    /**
     * What the owner picks NEXT under an estate (`ResolveEstate`). Keyed by
     * ESTATE id, and a phase is an estate — so drilling into a phase is a new
     * key, not a mutation of the parent's, and going back up is a cache hit.
     */
    resolve: (id: EstateId) => ['estate-resolve', id] as const,
    /**
     * An estate page's per-view Google Places photos (D14). Keyed by the
     * estate's public id; never persisted beyond the in-memory query cache.
     */
    photos: (estatePublicId: PublicId) => ['estate-photos', estatePublicId] as const,
  },

  // ---------------------------------------------------------------------------
  // Payments (ADR 0151 — the wizard publication-fee step)
  // ---------------------------------------------------------------------------
  payments: {
    /** Root key for all payment queries */
    all: ['payments'] as const,
    /** The listing's webhook-written payment status */
    status: (listingId: ListingId) => ['payments', 'status', listingId] as const,
  },

  // ---------------------------------------------------------------------------
  // Listings
  // ---------------------------------------------------------------------------
  listings: {
    /** Root key for all listing-related queries */
    all: ['listings'] as const,
    /**
     * Map view listings.
     *
     * ⚠ THE PARAMETERS ARE TYPED AND GUARDED, AND THAT IS THE POINT (R4-2).
     * They were `bounds: unknown, filters: unknown` — the SAME hole as the one
     * that sat on the BFF request body, one layer up. TanStack's `hashKey` is
     * `JSON.stringify` with a key-sorting replacer, so a `bigint` anywhere in
     * a query key throws exactly as it does on the wire, and `unknown` accepts
     * one silently.
     *
     * The trailing `JsonSafeGuard` is `[]` — omittable, so no call site
     * changes — while every leaf of the tuple survives `JSON.stringify`, and
     * becomes a REQUIRED, unsatisfiable argument the moment one stops. It is
     * the same door `server/loader.ts` and `server/api/json.ts` put on the two
     * SERVER JSON exits, spread over a parameter TUPLE rather than one value.
     */
    map: (
      mode: TransactionModeValue,
      bounds: MapBounds | undefined,
      filters: ListingsFilterSet,
      ...guard: JsonSafeGuard<[TransactionModeValue, MapBounds | undefined, ListingsFilterSet]>
    ) => {
      void guard;
      return ['map-listings', mode, bounds, filters] as const;
    },
  },

  // ---------------------------------------------------------------------------
  // Search
  // ---------------------------------------------------------------------------
  search: {
    /** Root key for all search-related queries */
    all: ['search'] as const,
    /** Search autocomplete suggestions */
    suggestions: (query: string, limit: number) => ['search-suggestions', query, limit] as const,
  },

  // ---------------------------------------------------------------------------
  // Conversations
  // ---------------------------------------------------------------------------
  conversations: {
    /**
     * Root key for all conversation-related queries.
     *
     * Prefix-matching parent of `list`, `unreadCount` and `thread`. NOTE it is
     * deliberately NOT what the realtime bindings invalidate: `thread` is a
     * write-only store with `queryFn: skipToken`, and TanStack's
     * `invalidateQueries` refetches every ACTIVE match — which for a skipToken
     * query means `ensureQueryFn` logs "Attempted to invoke queryFn when set to
     * skipToken" and rejects the fetch. So the bindings name the two fetching
     * keys explicitly; this root stays the door for a coarse "everything
     * conversational is stale" (e.g. sign-out teardown).
     */
    all: ['conversations'] as const,
    /**
     * The inbox/sidebar conversation list (W-6).
     *
     * The tab badge was live (react-query + realtime invalidation) while the
     * sidebar rendered a LOADER snapshot, so the two disagreed constantly:
     * opening a thread cleared the badge but left the row bold, and a message
     * in another conversation bumped the badge while the sidebar showed
     * nothing. Both now derive from queries under this root, and the same
     * realtime binding refreshes them together.
     *
     * Unparameterised: this is the FIRST page at `CONVERSATIONS_PAGE_SIZE` —
     * what the thread sidebar shows. The index page's pager owns its own
     * paginated loader data and is not this key.
     */
    list: ['conversations', 'list'] as const,
    /**
     * The Messages tab-badge unread-conversations count (R5: its own query).
     *
     * W-5: NESTED under the root, so a root invalidation subsumes it by
     * TanStack's prefix matching. It used to be the sibling key
     * `['conversations-unread-count']` — which merely LOOKS nested — so the
     * root invalidation this factory's shape advertises silently missed the
     * badge, and every caller had to remember to invalidate both.
     */
    unreadCount: ['conversations', 'unread-count'] as const,
    /**
     * The message list of ONE conversation thread (D-W3 step-2).
     *
     * The single store for a thread's messages — the loader snapshot seeds it
     * (`initialData`), realtime appends/upserts into it, and optimistic sends
     * write-and-roll-back through it. Replaces ConversationThread's former dual
     * `useState(initialMessages)` + `optimisticMessages` state, so a
     * same-conversation loader revalidation (no remount) now flows into the
     * thread instead of being silently ignored. NESTED under the root, but see
     * the `all` note: it has NO `queryFn`, so it must never be reached by an
     * invalidation that expects to refetch.
     *
     * The shape is the shared `messageThreadKey` factory
     * (`@letsgethome/lib/conversations/message-store`) — the SSOT both this
     * key and any writer into the thread store name — kept generic so the
     * branded `ConversationId` flows through with its brand intact.
     */
    thread: (conversationId: ConversationId) => messageThreadKey(conversationId),
  },

  // ---------------------------------------------------------------------------
  // Saved items (SV-C1)
  // ---------------------------------------------------------------------------
  saved: {
    /** Root key for all saved-state overlay entries. */
    all: ['saved'] as const,
    /**
     * The shared save-state OVERLAY for one saveable (`listing:<id>` /
     * `estate:<id>`). This cache slot is written ONLY by the save mutation
     * (optimistic on click, reverted on error); every SaveButton for the same
     * entity — the search card, the map popup, the detail header — reads this
     * one slot, so their hearts cannot diverge. Reads fall back to the server
     * value when the overlay is unset. There is no queryFn: it is a write-only
     * shared store, not a fetch.
     */
    overlay: (key: SaveableKey) => ['saved', 'overlay', key] as const,
  },

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------
  filters: {
    /** Root key for all filter-related queries */
    all: ['filters'] as const,
    /**
     * Filters metadata (histogram, counts) — the modal's preview query.
     *
     * Spelled out rather than `(...args: unknown[])`, for the reason the map
     * key above carries: `unknown[]` is a key factory that accepts a `bigint`,
     * and `hashKey` would throw on it. Naming the scope and the refinements
     * also makes the key and the request body ONE declaration read twice — the
     * modal builds both from the same locals.
     */
    metadata: (
      mode: TransactionModeValue,
      estateId: EstateId | undefined,
      districtId: DistrictId | undefined,
      minPriceCents: ExactInt | null,
      maxPriceCents: ExactInt | null,
      bedrooms: number | null,
      bathrooms: number | null,
      petsAllowed: boolean,
      ...guard: JsonSafeGuard<
        [
          TransactionModeValue,
          EstateId | undefined,
          DistrictId | undefined,
          ExactInt | null,
          ExactInt | null,
          number | null,
          number | null,
          boolean,
        ]
      >
    ) => {
      void guard;
      return [
        'filters-metadata',
        mode,
        estateId,
        districtId,
        minPriceCents,
        maxPriceCents,
        bedrooms,
        bathrooms,
        petsAllowed,
      ] as const;
    },
  },
} as const;
